import { useState, useEffect } from 'react';
import { HoldingsPerson, formatCents } from '../../types';
import { HoldingsCompensation, transformCompensation, EMPLOYMENT_TYPES, COMPENSATION_STATUSES } from '../../types/compensation';
import { Button, Input, Textarea } from "@repo/o37-ui";
import { X, Plus, DollarSign, Edit2 } from "lucide-react";
import CompensationForm from './CompensationForm';

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";

interface PersonFormProps {
  person?: HoldingsPerson | null;
  onClose: () => void;
  onSaved: () => void;
}

export default function PersonForm({ person, onClose, onSaved }: PersonFormProps) {
  const isEditing = !!person;

  const [name, setName] = useState(person?.name || '');
  const [email, setEmail] = useState(person?.email || '');
  const [phone, setPhone] = useState(person?.phone || '');
  const [notes, setNotes] = useState(person?.notes || '');
  const [isActive, setIsActive] = useState(person?.isActive !== false);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Tab state
  const [activeTab, setActiveTab] = useState<'details' | 'compensation'>('details');

  // Compensation state
  const [compensation, setCompensation] = useState<HoldingsCompensation | null>(null);
  const [showCompensationForm, setShowCompensationForm] = useState(false);
  const [loadingCompensation, setLoadingCompensation] = useState(false);

  const userId = localStorage.getItem('app_user_id') || '';
  const userRole = localStorage.getItem('app_role') || 'user';

  useEffect(() => {
    if (isEditing && person?.id) {
      loadCompensation();
    }
  }, []);

  const loadCompensation = async () => {
    if (!person?.id) return;
    setLoadingCompensation(true);
    try {
      const response = await fetch(`${API_URL}/holdings/compensation?person_id=${person.id}`, {
        headers: { 'x-user-id': userId, 'x-user-role': userRole }
      });
      if (response.ok) {
        const data = await response.json();
        // Find compensation for this person
        const personComp = data.find((c: any) => c.person_id === person.id);
        if (personComp) {
          setCompensation(transformCompensation(personComp));
        }
      }
    } catch (e) {
      console.error('Failed to load compensation:', e);
    } finally {
      setLoadingCompensation(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const payload = {
        name: name.trim(),
        email: email.trim() || null,
        phone: phone.trim() || null,
        notes: notes.trim() || null,
        isActive
      };

      const url = isEditing
        ? `${API_URL}/holdings/people/${person.id}`
        : `${API_URL}/holdings/people`;

      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
          'x-user-role': userRole
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        onSaved();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to save person');
      }
    } catch (e) {
      setError('Failed to save person');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-100">
          <h2 className="text-xl font-semibold">
            {isEditing ? 'Edit Person' : 'Add Person'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-neutral-100 rounded-lg">
            <X className="w-5 h-5 text-neutral-500" />
          </button>
        </div>

        {/* Tabs (only show when editing) */}
        {isEditing && (
          <div className="flex items-center gap-1 p-2 mx-6 mt-4 bg-neutral-100 rounded-lg w-fit">
            <button
              type="button"
              onClick={() => setActiveTab('details')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'details'
                  ? 'bg-white text-neutral-900 shadow-sm'
                  : 'text-neutral-600 hover:text-neutral-900'
              }`}
            >
              Details
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('compensation')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                activeTab === 'compensation'
                  ? 'bg-white text-neutral-900 shadow-sm'
                  : 'text-neutral-600 hover:text-neutral-900'
              }`}
            >
              <DollarSign className="w-4 h-4" />
              Compensation
              {compensation && (
                <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">
                  {formatCents(compensation.totalAnnualCostCents)}/yr
                </span>
              )}
            </button>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="p-3 bg-red-50 text-red-600 text-sm rounded-lg border border-red-200">
              {error}
            </div>
          )}

          {/* DETAILS TAB */}
          {(activeTab === 'details' || !isEditing) && (
            <>
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Name *
            </label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="John Doe"
              required
            />
          </div>

          {/* Contact Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Email
              </label>
              <Input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="john@example.com"
                type="email"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Phone
              </label>
              <Input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+1 (555) 123-4567"
                type="tel"
              />
            </div>
          </div>

          {/* Active Status */}
          <div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="rounded border-neutral-300"
              />
              <span className="font-medium text-neutral-700">Active</span>
              <span className="text-neutral-500">(uncheck to mark as inactive)</span>
            </label>
          </div>

          {/* Notes (in Details tab) */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Notes
            </label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional notes about this person..."
              rows={3}
            />
          </div>
            </>
          )}

          {/* COMPENSATION TAB */}
          {activeTab === 'compensation' && isEditing && (
            <div className="space-y-4">
              {loadingCompensation ? (
                <div className="p-8 text-center text-neutral-500">Loading compensation...</div>
              ) : compensation ? (
                <div className="space-y-4">
                  {/* Compensation Summary */}
                  <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="text-sm font-medium text-emerald-800">Total Annual Cost</div>
                        <div className="text-2xl font-bold text-emerald-700">
                          {formatCents(compensation.totalAnnualCostCents)}
                        </div>
                        <div className="text-sm text-emerald-600">
                          {formatCents(Math.round(compensation.totalAnnualCostCents / 12))}/month
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setShowCompensationForm(true)}
                        icon={<Edit2 className="w-4 h-4" />}
                        text="Edit"
                      />
                    </div>
                  </div>

                  {/* Compensation Breakdown */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-neutral-50 rounded-lg">
                      <div className="text-xs text-neutral-500 mb-1">Base Salary</div>
                      <div className="font-semibold">{formatCents(compensation.baseSalaryCents)}/yr</div>
                    </div>
                    <div className="p-3 bg-neutral-50 rounded-lg">
                      <div className="text-xs text-neutral-500 mb-1">Target Bonus</div>
                      <div className="font-semibold">{formatCents(compensation.targetBonusCents)}/yr</div>
                    </div>
                    <div className="p-3 bg-neutral-50 rounded-lg">
                      <div className="text-xs text-neutral-500 mb-1">Equity Value</div>
                      <div className="font-semibold">{formatCents(compensation.equityValueCents)}/yr</div>
                    </div>
                    <div className="p-3 bg-neutral-50 rounded-lg">
                      <div className="text-xs text-neutral-500 mb-1">Benefits</div>
                      <div className="font-semibold">{formatCents(compensation.benefitsCents)}/yr</div>
                    </div>
                    <div className="p-3 bg-neutral-50 rounded-lg">
                      <div className="text-xs text-neutral-500 mb-1">Employer Taxes</div>
                      <div className="font-semibold">{formatCents(compensation.employerTaxesCents)}/yr</div>
                    </div>
                    <div className="p-3 bg-neutral-50 rounded-lg">
                      <div className="text-xs text-neutral-500 mb-1">Company</div>
                      <div className="font-semibold">{compensation.company?.name || 'Unknown'}</div>
                    </div>
                  </div>

                  {/* Employment Details */}
                  <div className="p-4 bg-neutral-50 rounded-lg space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-neutral-500">Employment Type</span>
                      <span className="font-medium">
                        {EMPLOYMENT_TYPES.find(t => t.type === compensation.employmentType)?.label || compensation.employmentType}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-neutral-500">Status</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        COMPENSATION_STATUSES.find(s => s.status === compensation.status)?.color || ''
                      }`}>
                        {COMPENSATION_STATUSES.find(s => s.status === compensation.status)?.label || compensation.status}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-neutral-500">Effective Date</span>
                      <span className="font-medium">{new Date(compensation.effectiveDate).toLocaleDateString()}</span>
                    </div>
                    {compensation.title && (
                      <div className="flex justify-between text-sm">
                        <span className="text-neutral-500">Title</span>
                        <span className="font-medium">{compensation.title}</span>
                      </div>
                    )}
                    {compensation.department && (
                      <div className="flex justify-between text-sm">
                        <span className="text-neutral-500">Department</span>
                        <span className="font-medium">{compensation.department}</span>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="p-8 text-center">
                  <DollarSign className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">No Compensation Record</h3>
                  <p className="text-neutral-500 mb-4">Add compensation details for this person.</p>
                  <Button
                    type="button"
                    onClick={() => setShowCompensationForm(true)}
                    icon={<Plus className="w-4 h-4" />}
                    text="Add Compensation"
                  />
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-neutral-100">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving} text="Cancel" />
            <Button type="submit" disabled={saving} text={saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Add Person'} />
          </div>
        </form>
      </div>

      {/* Compensation Form Modal */}
      {showCompensationForm && person && (
        <CompensationForm
          personId={person.id}
          personName={person.name}
          compensation={compensation}
          onClose={() => setShowCompensationForm(false)}
          onSaved={() => {
            setShowCompensationForm(false);
            loadCompensation();
          }}
        />
      )}
    </div>
  );
}
